import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import toast from 'react-hot-toast'
import { LandingHero } from '@/components/landing/LandingHero'
import { MethodologyTimeline } from '@/components/landing/MethodologyTimeline'
import { LegalDisclaimer } from '@/components/landing/LegalDisclaimer'
import { LandingFAQ } from '@/components/landing/LandingFAQ'
import { ResearcherProfileDrawer } from '@/components/landing/ResearcherProfileDrawer'
import { ContactSection } from '@/components/landing/ContactSection'
import { AcademicCapIcon, ShieldCheckIcon, HandRaisedIcon } from '@heroicons/react/24/outline'

export default function Login() {
  const navigate = useNavigate()
  const login = useAppStore(s => s.login)
  const register = useAppStore(s => s.register)
  const telemetryConsent = useAppStore(s => s.telemetryConsent)
  const setTelemetryConsent = useAppStore(s => s.setTelemetryConsent)
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const authCardRef = useRef<HTMLDivElement | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (mode === 'login') {
        await login(email, password)
        toast.success('Logged in')
      } else {
        await register(email, password)
        toast.success('Account created')
      }
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      toast.error(err?.message || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const scrollToAuthCard = () => {
    authCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const scrollToMethodology = () => {
    const section = document.getElementById('methodology')
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const evidenceHighlights = [
    {
      icon: AcademicCapIcon,
      title: 'Identity-bound authentication',
      description: 'Access tokens stay tied to verified work or academic email accounts so only approved operators enter the lab.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Ownership verification built in',
      description: 'HTTP proof files and DNS TXT challenges gate live tooling until you demonstrate control of each asset you plan to scan.',
    },
    {
      icon: HandRaisedIcon,
      title: 'Policy-first onboarding',
      description: 'Teams acknowledge responsible-use expectations before touching sandbox tools, keeping audits focused on guardrails—not guesswork.',
    },
  ]

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="relative overflow-hidden pb-16">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-500" aria-hidden />
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col gap-16 px-4 pt-16 sm:px-6 lg:px-8 lg:pt-20">
          <div className="grid items-start gap-14 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <LandingHero
              onLaunchDemo={scrollToAuthCard}
              onViewMethodology={scrollToMethodology}
              onOpenProfile={() => setProfileOpen(true)}
            />
            <div
              ref={authCardRef}
              id="auth-card"
              className="w-full rounded-2xl border border-blue-500/40 bg-gray-900/80 p-8 shadow-2xl shadow-blue-900/40 backdrop-blur"
            >
              <div className="mb-6 space-y-2 text-center">
                <p className="text-xs uppercase tracking-wide text-blue-300/80">Zero-trust access portal</p>
                <h1 className="text-2xl font-semibold text-white">
                  {mode === 'login' ? 'Sign in to the safeguarded workspace' : 'Request researcher access'}
                </h1>
                <p className="text-sm text-gray-400">
                  {mode === 'login'
                    ? 'Authenticate with your approved email to confirm you are part of an authorised training or security team before resuming controlled workstreams inside the sandbox. Target tooling unlocks only after ownership is verified.'
                    : 'New operators complete acceptable-use onboarding so every participant aligns on policy before tooling is issued, including how to prove control of the assets they scan.'}
                </p>
              </div>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-300" htmlFor="email">Work or institutional email</label>
                  <input
                    type="email"
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    id="email"
                    name="email"
                    placeholder="you@organisation.edu"
                    aria-label="Email"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-300" htmlFor="password">Password</label>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    id="password"
                    name="password"
                    placeholder="••••••••"
                    aria-label="Password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-blue-600 py-2 text-sm font-semibold text-white transition hover:bg-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Please wait…' : mode === 'login' ? 'Secure sign in' : 'Submit access request'}
                </button>
              </form>
              <div className="mt-4 text-center text-sm text-gray-400">
                {mode === 'login' ? (
                  <button className="text-blue-300 hover:text-blue-200" onClick={() => setMode('register')}>
                    Need access? Request operator credentials
                  </button>
                ) : (
                  <button className="text-blue-300 hover:text-blue-200" onClick={() => setMode('login')}>
                    Already provisioned? Sign in
                  </button>
                )}
              </div>
              <div className="mt-6 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-xs text-blue-100">
                <p className="font-semibold uppercase tracking-wide text-[0.65rem]">Responsible use notice</p>
                <p className="mt-1">
                  Access is monitored and attributable to protect learners, security teams, and approved targets. Only demo systems or
                  assets you verify ownership of may be scanned. Review the{' '}
                  <Link className="text-blue-200 underline hover:text-white" to="/acceptable-use">responsible use charter</Link>{' '}
                  before continuing.
                </p>
              </div>
              <div className="mt-4 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 text-xs text-blue-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold uppercase tracking-wide text-[0.65rem]">Telemetry consent</p>
                    <p className="mt-1 text-[0.7rem]">
                      Share anonymized runtime metrics to help us spot aborted sessions and reliability gaps faster. We only send
                      these signals after you opt in.
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-blue-100">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border border-blue-400 bg-transparent text-blue-500 focus:ring-blue-400"
                      checked={telemetryConsent === 'granted'}
                      onChange={(event) => setTelemetryConsent(event.target.checked ? 'granted' : 'denied')}
                    />
                    <span>Share anonymized telemetry</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {evidenceHighlights.map(({ icon: Icon, title, description }) => (
              <div key={title} className="rounded-2xl border border-gray-800 bg-gray-900/70 p-6 shadow-inner shadow-black/20">
                <div className="mb-3 inline-flex items-center justify-center rounded-full border border-blue-400/30 bg-blue-500/10 p-3 text-blue-200">
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm text-gray-400">{description}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-gray-950 via-gray-950/80 to-transparent" aria-hidden />
      </div>

      <LegalDisclaimer />

      <MethodologyTimeline />

      <LandingFAQ />

  <ContactSection />

      <ResearcherProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />

      <section className="bg-gray-950 pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-800 bg-gray-900/80 p-8 sm:p-12">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold text-white sm:text-3xl">Structured for compliance leaders, ready for classrooms and security teams</h2>
                <p className="text-sm text-gray-300 sm:text-base">
                  Every SQLi scan action is logged, every demo host is vetted, and every flag bundle ships with explanatory
                  prose. Export audit packs before assessments, readiness reviews, or stakeholder briefings to demonstrate full compliance.
                </p>
              </div>
              <ul className="space-y-3 rounded-2xl border border-blue-400/30 bg-blue-500/5 p-6 text-sm text-blue-100">
                {[
                  'Download onboarding briefings for educators, security leads, and consulting partners.',
                  'Share quick reference cards mapping SQLMap toggles and SQLi payload types to learning or control-validation outcomes.',
                  'Bundle anonymised evidence for moderation, stakeholder reviews, or red-team readouts in minutes, including proof of target ownership.',
                ].map(point => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-blue-300" aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 flex flex-wrap justify-between gap-4 text-xs text-gray-500">
              <span>Audit-ready | GDPR mindful | Safe SQLi demo targets only</span>
              <span>Version 1.0.0 &middot; Controlled access track</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
